import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';

/**
 * „Uwagi od FTL” — klient API (`apps/api/src/ftl-notes/`, makieta back office
 * v2, BR-FTL-13 część, ścieżka FTL-05; 20.10.2026). Słownik kategorii
 * i stanów przychodzi z API razem z uwagami — front nie ma własnej kopii.
 */

/** Klucz przełącznika (rejestr `feature-flags.ts` w API). */
export const FEATURE_FTL_NOTES = 'ftlNotes';

/** Licznik w menu (otwarte uwagi). */
export const FTL_NOTES_COUNT_URL = '/ftl-notes/count';

/**
 * Zdarzenie okna „uwagi się zmieniły” — licznik w menu odświeża się od razu
 * po dodaniu uwagi albo zmianie stanu, zamiast czekać na swój cykl.
 */
export const FTL_NOTES_CHANGED_EVENT = 'ftl-notes-changed';

export const notifyFtlNotesChanged = (): void => {
  window.dispatchEvent(new Event(FTL_NOTES_CHANGED_EVENT));
};

export interface FtlNotePerson {
  id: number;
  name: string;
}

export interface FtlNoteOrder {
  id: number;
  number: string | null;
  fromCity: string | null;
  fromCountry: string | null;
  toCity: string | null;
  toCountry: string | null;
  fromDate: string | null;
  planningStatus: string;
}

export interface FtlNote {
  id: number;
  userOfferId: number;
  category: string;
  categoryLabel: string;
  content: string;
  urgent: boolean;
  /** Kod stanu — dane z bazy (`new` / `in_progress` / `resolved`). */
  status: string;
  statusLabel: string;
  /** Dokąd odbiorca może przestawić uwagę. */
  next: string[];
  createdAt: string;
  createdBy: FtlNotePerson | null;
  statusChangedAt: string | null;
  statusChangedBy: FtlNotePerson | null;
  statusComment: string | null;
  /** Tylko na liście odbiorcy. */
  order?: FtlNoteOrder;
}

export interface FtlNoteDictionary {
  categories: Array<{ code: string; label: string }>;
  statuses: Array<{ code: string; label: string; next: string[] }>;
}

export interface OrderNotes extends FtlNoteDictionary {
  notes: FtlNote[];
}

export interface FtlNotesInbox extends FtlNoteDictionary {
  data: FtlNote[];
  total: number;
  limit: number;
}

/** Filtr listy odbiorcy — `open` (domyślnie), jeden stan albo `all`. */
export type InboxFilter = 'open' | 'new' | 'in_progress' | 'resolved' | 'all';

export async function fetchOrderNotes(userOfferId: number | string): Promise<OrderNotes> {
  const { data } = await axiosNest.get<OrderNotes>(`/ftl-notes/order/${userOfferId}`);
  return data;
}

export async function fetchFtlNotesInbox(status: InboxFilter = 'open'): Promise<FtlNotesInbox> {
  const { data } = await axiosNest.get<FtlNotesInbox>('/ftl-notes', { params: { status } });
  return data;
}

export async function fetchOpenFtlNotes(): Promise<{ open: number; urgent: number }> {
  const { data } = await axiosNest.get<{ open: number; urgent: number }>(FTL_NOTES_COUNT_URL);
  return data;
}

export async function createFtlNote(input: {
  userOfferId: number;
  category: string;
  content: string;
  urgent: boolean;
}): Promise<FtlNote> {
  const { data } = await axiosNest.post<FtlNote>('/ftl-notes', input);
  return data;
}

export async function changeFtlNoteStatus(id: number, status: string, comment: string): Promise<FtlNote> {
  const { data } = await axiosNest.patch<FtlNote>(`/ftl-notes/${id}/status`, { status, comment });
  return data;
}

/**
 * Stan końcowy „zakończona” (21.10.2026, S-21.10-26, przełącznik
 * `ftlDecisionNotifications`; paczka p. Mariusza 21.10, BR-FTL-13) — dotąd
 * „załatwiona”. Kod stanu bez zmian (`resolved`).
 */
export const FEATURE_FTL_DECISION_NOTIFICATIONS = 'ftlDecisionNotifications';

/** Podpis przycisku zmiany stanu — czynność, nie nazwa stanu. */
export function statusActionLabel(from: string, to: string, ended = false): string {
  if (to === 'in_progress') return from === 'resolved' ? 'Wznów' : 'W toku';
  if (to === 'resolved') return ended ? 'Zakończona' : 'Załatwiona';
  return to;
}

export { toErrorMessage };
